import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Loader2, RefreshCw, User, MapPin, AlertCircle, Download, FileText, Camera } from 'lucide-react';
import { PipelineVisual } from '../PipelineVisual';
import { WhatsAppOutputs } from './WhatsAppOutputs';
import { RadioOutputs } from './RadioOutputs';
import { SocialOutputs } from './SocialOutputs';
import { VideoOutputs } from './VideoOutputs';
import { PolicyOutput, MediaOutput } from './PolicyMediaOutputs';
import { ImpactSignals, EvidenceExtraction } from './EvidenceImpact';
import { QueueButtons } from './QueueButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'whatsapp', label: 'WhatsApp', channel: 'whatsapp' },
  { key: 'radio', label: 'Radio', channel: 'radio' },
  { key: 'social', label: 'Instagram / Facebook', channel: 'social' },
  { key: 'video', label: 'Short Video', channel: 'video' },
  { key: 'policy', label: 'Policy Insight', channel: 'policy' },
  { key: 'media', label: 'Media Story', channel: 'media' },
  { key: 'evidence', label: 'Evidence Extraction', channel: 'evidence' },
  { key: 'signals', label: 'Impact Signals', channel: 'evidence' },
];

export const GeneratedOutputCard = ({ story, token, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [section, setSection] = useState('whatsapp');
  const [error, setError] = useState(null);
  const [replacingImage, setReplacingImage] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/stories/${story.id}/outputs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [story.id, token]);

  useEffect(() => { load(); }, [load]);

  const regenerate = async () => {
    setRegenerating(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/stories/${story.id}/generate-outputs`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${res.status})`);
      }
      await load();
      onRefresh && onRefresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setRegenerating(false);
    }
  };

  const downloadBundle = async () => {
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/stories/${story.id}/outputs/bundle`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safe = (story.title || 'story').replace(/[^a-z0-9_\- ]/gi, '').replace(/\s+/g, '_').slice(0, 60);
      a.href = url;
      a.download = `${safe}_outputs.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Refresh to update download_count + history
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const downloadChannelDocx = async (channel) => {
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/stories/${story.id}/outputs/docx/${channel}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safe = (story.title || 'story').replace(/[^a-z0-9_\- ]/gi, '').replace(/\s+/g, '_').slice(0, 60);
      a.href = url;
      a.download = `${safe}_${channel}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    }
  };

  const replaceImage = async (file) => {
    if (!file) return;
    setError(null);
    setReplacingImage(true);
    try {
      // 1. Upload the new image
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'stories');
      const upRes = await fetch(`${API_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!upRes.ok) {
        const err = await upRes.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${upRes.status})`);
      }
      const { url: newUrl } = await upRes.json();
      // 2. PATCH the story
      const patchRes = await fetch(`${API_URL}/api/admin/stories/${story.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ image_url: newUrl }),
      });
      if (!patchRes.ok) {
        const err = await patchRes.json().catch(() => ({}));
        throw new Error(err.detail || `Update failed (${patchRes.status})`);
      }
      // 3. Bubble up so parent re-fetches the story (image swaps in the UI)
      onRefresh && onRefresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setReplacingImage(false);
    }
  };

  const outputs = data?.generated_outputs || {};
  const hasOutputs = !!outputs.generated_version;
  const generating = data?.generation_status === 'running' || regenerating;

  return (
    <Card data-testid={`generated-output-card-${story.id}`} className="overflow-hidden">
      <div className="grid md:grid-cols-[180px_1fr] gap-0">
        {story.image_url && (
          <div className="hidden md:block relative group">
            <img src={story.image_url} alt={story.title} className="w-full h-full object-cover" style={{ minHeight: '100%' }} onError={(e) => { const fb = 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=400'; if (e.currentTarget.src !== fb) e.currentTarget.src = fb; }} />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              data-testid={`replace-image-input-${story.id}`}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (f) replaceImage(f);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={replacingImage}
              data-testid={`replace-image-btn-${story.id}`}
              title="Replace image"
              className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/0 group-hover:bg-black/55 transition text-white text-xs font-semibold opacity-0 group-hover:opacity-100 disabled:opacity-100"
            >
              {replacingImage ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
              ) : (
                <><Camera className="w-4 h-4" /> Replace image</>
              )}
            </button>
          </div>
        )}
        <CardContent className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1">
              <h3 className="font-serif text-xl font-bold text-gray-900 leading-tight">{story.title}</h3>
              <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-600">
                <span className="flex items-center gap-1"><User className="w-3 h-3" />{story.author_name}</span>
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{story.country_name}</span>
                {story.story_archetype && <Badge className="bg-[#E4E517] text-gray-900 hover:bg-[#E4E517]">{story.story_archetype}</Badge>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasOutputs && (
                <button
                  type="button"
                  onClick={downloadBundle}
                  data-testid={`download-bundle-${story.id}`}
                  className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#07743F] text-white hover:bg-[#065a32] transition"
                  title="Download all outputs as a single ZIP"
                >
                  <Download className="w-3 h-3" /> Download ZIP
                </button>
              )}
              <button
                type="button"
                onClick={regenerate}
                disabled={generating}
                data-testid={`regenerate-${story.id}`}
                className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-300 hover:border-[#07743F] hover:text-[#07743F] transition disabled:opacity-50"
              >
                {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {hasOutputs ? 'Regenerate' : 'Generate'}
              </button>
            </div>
          </div>

          {/* Pipeline */}
          <div className="mb-3">
            <PipelineVisual pipeline={data?.pipeline} generationStatus={generating ? 'running' : data?.generation_status} />
          </div>

          {/* Queue buttons */}
          <div className="mb-4">
            <QueueButtons
              storyId={story.id}
              token={token}
              queues={data?.output_queues}
              onChange={(q) => setData((d) => ({ ...d, output_queues: q }))}
            />
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded p-2">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {loading && !data && (
            <div className="flex items-center gap-2 text-gray-500 py-4 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading outputs…
            </div>
          )}

          {data && !hasOutputs && !generating && (
            <div className="text-sm text-gray-500 py-4">
              No outputs generated yet. Click <strong>Generate</strong> above to produce the 11 communication assets (~60s).
            </div>
          )}

          {generating && (
            <div className="flex items-center gap-2 text-gray-700 py-4 text-sm bg-[#E4E517]/20 border border-[#E4E517] rounded p-3">
              <Loader2 className="w-4 h-4 animate-spin" />
              Generating WhatsApp, radio, social, video, policy, media, evidence & impact signals… (~60s)
            </div>
          )}

          {hasOutputs && (
            <>
              {/* Section tabs */}
              <div className="flex flex-wrap gap-1 mb-4 border-b border-gray-200">
                {SECTIONS.map((s) => {
                  const active = section === s.key;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      data-testid={`output-tab-${s.key}`}
                      onClick={() => setSection(s.key)}
                      className={`px-3 py-2 text-xs font-semibold border-b-2 transition ${
                        active
                          ? 'border-[#07743F] text-[#07743F]'
                          : 'border-transparent text-gray-500 hover:text-gray-800'
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>

              {(() => {
                const cur = SECTIONS.find((s) => s.key === section);
                if (!cur) return null;
                return (
                  <div className="flex justify-end mb-3">
                    <button
                      type="button"
                      onClick={() => downloadChannelDocx(cur.channel)}
                      data-testid={`download-docx-${cur.key}-${story.id}`}
                      className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#07743F]/30 text-[#07743F] hover:bg-[#07743F] hover:text-white transition"
                      title={`Download ${cur.label} as a Microsoft Word (.docx) document`}
                    >
                      <FileText className="w-3 h-3" /> Download Word (.docx)
                    </button>
                  </div>
                );
              })()}

              {section === 'whatsapp' && <WhatsAppOutputs data={outputs.whatsapp} />}
              {section === 'radio' && <RadioOutputs data={outputs.radio} />}
              {section === 'social' && <SocialOutputs data={outputs.social} />}
              {section === 'video' && <VideoOutputs data={outputs.video} />}
              {section === 'policy' && <PolicyOutput data={outputs.policy_brief} />}
              {section === 'media' && <MediaOutput data={outputs.media_story} />}
              {section === 'evidence' && <EvidenceExtraction data={outputs.evidence_extraction} />}
              {section === 'signals' && <ImpactSignals data={outputs.impact_signals} />}

              {/* Repurposing history */}
              {Array.isArray(data.repurposing_history) && data.repurposing_history.length > 0 && (
                <details className="mt-5 pt-4 border-t border-gray-100">
                  <summary className="text-xs font-semibold text-gray-600 cursor-pointer">
                    Repurposing history ({data.repurposing_history.length})
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {data.repurposing_history.slice(-10).reverse().map((h, i) => (
                      <li key={i} className="text-xs text-gray-500">
                        <span className="font-mono">{(h.at || '').slice(0, 16).replace('T', ' ')}</span>
                        {' · '}
                        <span>{h.action}</span>
                        {h.by && <span> · by {h.by}</span>}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </CardContent>
      </div>
    </Card>
  );
};

export default GeneratedOutputCard;
